import { Modal } from "react-bootstrap";

import { eligibilityFor } from "../partnerUserEligibility";
import { usePartnerHasSoloUser } from "../../../hooks/usePartnerHasSoloUser";

const statusBadgeClass = (status) => {
  const value = status?.toLowerCase();
  if (value === "active")    return "badge-success";
  if (value === "pending")   return "badge-warning";
  if (value === "inactive")  return "badge-danger";
  if (value === "suspended") return "badge-light";
  return "badge-secondary";
};

/**
 * Centered modal that opens on the three-dot button in the users table and
 * offers only the actions the viewer is allowed to take on that row. Closes
 * before firing an action so the parent's own modals (edit, delete confirm,
 * limits, etc.) can open cleanly.
 */
const PartnerUserActionsModal = ({
  partnerUser,
  onClose,
  authUser,
  canManageUsers,
  canManageTransactionLimits,
  onViewInfo,
  onManageTransactionLimits,
  onEditUser,
  onActivateWallet,
  onAssignTeam,
  onChangeRole,
  onDeleteUser,
  onResendVerification,
  onFixEmail,
}) => {
  const runAction = (fn) => {
    const target = partnerUser;
    onClose();
    if (target) fn(target);
  };

  const { hasSoloUser } = usePartnerHasSoloUser(authUser?.partner_id);

  const elig = partnerUser
    ? eligibilityFor(partnerUser, {
        authUser,
        canManageUsers,
        canManageTransactionLimits,
        actions: {
          onViewInfo,
          onManageTransactionLimits,
          onEditUser,
          onActivateWallet,
          onAssignTeam,
          onChangeRole,
          onDeleteUser,
          onResendVerification,
          onFixEmail,
        },
      })
    : null;

  return (
    <Modal show={!!partnerUser} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{partnerUser?.full_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {partnerUser && elig && (
          <div className="d-flex flex-column gap-3">
            <div className="row g-2">
              <div className="col-sm-4 text-muted">Role</div>
              <div className="col-sm-8 fw-bold" style={{ color: '#212529' }}>{partnerUser.role}</div>
            </div>
            <div className="row g-2">
              <div className="col-sm-4 text-muted">Email</div>
              <div className="col-sm-8 fw-bold" style={{ overflowWrap: 'anywhere', color: '#212529' }}>{partnerUser.email}</div>
            </div>
            {partnerUser.phone && (
              <div className="row g-2">
                <div className="col-sm-4 text-muted">Phone</div>
                <div className="col-sm-8 fw-bold" style={{ color: '#212529' }}>{partnerUser.phone}</div>
              </div>
            )}
            <div className="row g-2">
              <div className="col-sm-4 text-muted">Status</div>
              <div className="col-sm-8">
                <span className={`badge badge-rounded ${statusBadgeClass(partnerUser.status)}`}>
                  {partnerUser.status}
                </span>
              </div>
            </div>
            <div className="d-flex flex-column gap-2 pt-3 border-top">
              <p className="text-muted small mb-1">What would you like to do?</p>
              {elig.canView && (
                <button type="button" className="btn btn-outline-secondary text-start" onClick={() => runAction(onViewInfo)}>
                  <i className="fa fa-info-circle me-2" aria-hidden="true" />
                  More Details
                </button>
              )}
              {elig.canManageLimits && (
                <button type="button" className="btn btn-outline-secondary text-start" onClick={() => runAction(onManageTransactionLimits)}>
                  <i className="fa fa-sliders me-2" aria-hidden="true" />
                  Transaction Limit Management
                </button>
              )}
              {elig.canEdit && (
                <button type="button" className="btn btn-outline-info text-start" onClick={() => runAction(onEditUser)}>
                  <i className="fa fa-pencil-alt me-2" aria-hidden="true" />
                  Edit
                </button>
              )}
              {elig.canResendVerification && (
                <button type="button" className="btn btn-outline-primary text-start" onClick={() => runAction(onResendVerification)}>
                  <i className="fa fa-envelope me-2" aria-hidden="true" />
                  Resend verification pin
                </button>
              )}
              {elig.canFixEmail && (
                <button type="button" className="btn btn-outline-primary text-start" onClick={() => runAction(onFixEmail)}>
                  <i className="fa fa-at me-2" aria-hidden="true" />
                  Fix email
                </button>
              )}
              {elig.canActivate && (
                <button type="button" className="btn btn-outline-success text-start" onClick={() => runAction(onActivateWallet)}>
                  <i className="fa fa-wallet me-2" aria-hidden="true" />
                  Activate Wallet
                </button>
              )}
              {elig.canAssignTeam && !hasSoloUser && (
                <button type="button" className="btn btn-outline-info text-start" onClick={() => runAction(onAssignTeam)}>
                  <i className="fa fa-users me-2" aria-hidden="true" />
                  Assign Team
                </button>
              )}
              {elig.canChangeRole && (
                <button type="button" className="btn btn-outline-warning text-start" onClick={() => runAction(onChangeRole)}>
                  <i className="fa fa-user-shield me-2" aria-hidden="true" />
                  Change Role
                </button>
              )}
              {elig.canDelete && (
                <>
                  <hr className="my-1" />
                  <button type="button" className="btn btn-outline-danger text-start" onClick={() => runAction(onDeleteUser)}>
                    <i className="fa fa-trash-alt me-2" aria-hidden="true" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </Modal.Footer>
    </Modal>
  );
};

export default PartnerUserActionsModal;
